import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { ProtectedRoute, PublicRoute } from "./components/ProtectedRoute";

// Pages
import LandingPage from "./pages/LandingPage";
import RegisterPage from "./pages/RegisterPage";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage, { VerifyPendingPage } from "./pages/VerifyEmailPage";
import DashboardPage from "./pages/DashboardPage";
import OnboardingPage from "./pages/OnboardingPage";
import AdminOverview from "./pages/AdminOverview";
import AdminCandidates from "./pages/AdminCandidates";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminCandidateDetail from "./pages/AdminCandidateDetail";
import AdminUserSupport from "./pages/AdminUserSupport";
import AdminMessagesPage from "./pages/AdminMessagesPage";
import AccountSettingsPage from "./pages/AccountSettingsPage";
import ResumeManagementPage from "./pages/ResumeManagementPage";
import ProfilePage from "./pages/ProfilePage";
import MessageCenterPage from "./pages/MessageCenterPage";
import ReferralHubPage from "./pages/ReferralHubPage";
import BrandGuidePage from "./pages/BrandGuidePage";
import AdminMarketPulsePage from "./pages/AdminMarketPulsePage";
import MarketPulsePage from "./pages/MarketPulsePage";
import JobMatchesPage from "./pages/JobMatchesPage";
import HelpCenterPage from "./pages/HelpCenterPage";
import SupportTicketPage from "./pages/SupportTicketPage";
import MyTicketsPage from "./pages/MyTicketsPage";
import AdminGuidePage from "./pages/AdminGuidePage";
import AdminSupportTicketsPage from "./pages/AdminSupportTicketsPage";
import CareerInsightsPage from "./pages/CareerInsightsPage";
import AdminJobAnalyticsPage from "./pages/AdminJobAnalyticsPage";
import AdminJobImportPage from "./pages/AdminJobImportPage";

function App() {
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route
              path="/"
              element={
                <PublicRoute>
                  <LandingPage />
                </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            }
          />
          
          {/* Brand Guide - Public but unlisted (no navigation links) */}
          <Route path="/brand" element={<BrandGuidePage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          
          {/* Verification Pending */}
          <Route
            path="/verify-pending"
            element={
              <ProtectedRoute requireVerified={false}>
                <VerifyPendingPage />
              </ProtectedRoute>
            }
          />

          {/* Protected Candidate Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <AccountSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resumes"
            element={
              <ProtectedRoute>
                <ResumeManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <MessageCenterPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/referrals"
            element={
              <ProtectedRoute>
                <ReferralHubPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/market-pulse"
            element={
              <ProtectedRoute>
                <MarketPulsePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/matches"
            element={
              <ProtectedRoute>
                <JobMatchesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/career-insights"
            element={
              <ProtectedRoute>
                <CareerInsightsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help"
            element={
              <ProtectedRoute>
                <HelpCenterPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/support"
            element={
              <ProtectedRoute>
                <SupportTicketPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-tickets"
            element={
              <ProtectedRoute>
                <MyTicketsPage />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute adminOnly>
                <AdminOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/candidates"
            element={
              <ProtectedRoute adminOnly>
                <AdminCandidates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute adminOnly>
                <AdminAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/job-analytics"
            element={
              <ProtectedRoute adminOnly>
                <AdminJobAnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/candidates/:candidateId"
            element={
              <ProtectedRoute adminOnly>
                <AdminCandidateDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/support"
            element={
              <ProtectedRoute adminOnly>
                <AdminUserSupport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/messages"
            element={
              <ProtectedRoute adminOnly>
                <AdminMessagesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/market-pulse"
            element={
              <ProtectedRoute adminOnly>
                <AdminMarketPulsePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/guide"
            element={
              <ProtectedRoute adminOnly>
                <AdminGuidePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tickets"
            element={
              <ProtectedRoute adminOnly>
                <AdminSupportTicketsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/job-import"
            element={
              <ProtectedRoute adminOnly>
                <AdminJobImportPage />
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<LandingPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App;
